import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  OnInit,
  Type,
  ViewChild,
  createComponent,
  effect,
  inject,
  input,
  model,
  output,
  untracked,
} from '@angular/core';

import { CamCalendarEngine } from '../../engine/calendar-engine';
import { CamCalendarWidgetComponent } from '../../engine/components/calendar-widget/calendar-widget.component';
import { CamEventRecord } from '../../engine/models/event-record';
import {
  CamCalendarModesConfig,
  CamEventConfig,
  CamExternalEventSourceConfig,
  CamResourceConfig,
} from '../../engine/types';
import { CamTranslationCalendar } from '../../translation.service';
import { CamAddEventRequest, CamTimeRangeModel, CamUpdateEventRequest } from '../../types/calendar';
import { AbstractCalendarComponent } from '../abstract-calendar.component';

@Component({
  selector: 'cam-calendar',
  imports: [CamCalendarWidgetComponent],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CamCalendarComponent extends AbstractCalendarComponent implements OnInit, AfterViewInit {
  events = input.required<CamEventConfig[]>();

  timeRanges = input.required<CamTimeRangeModel[]>();

  cardComponent = input.required<Type<{ event: unknown }>>();

  resources = input<CamResourceConfig[]>();

  externalEventSourceFeature = model<CamExternalEventSourceConfig | boolean>(false);

  addEventRequest = output<CamAddEventRequest>();

  updateEventRequest = output<CamUpdateEventRequest>();

  calendarReady = output<CamCalendarEngine>();

  @ViewChild('calendarRef', { static: true })
  calendarRef!: CamCalendarWidgetComponent;
  @ViewChild('calendarContainer', { static: true })
  calendarContainer!: ElementRef<HTMLDivElement>;

  public modes: CamCalendarModesConfig;

  readonly currentDate = new Date();

  readonly timeRangesFeature = { headerWidth: 42 };

  protected readonly _translationSource = CamTranslationCalendar.getInstance();

  private readonly _appRef = inject(ApplicationRef);
  private readonly _environmentInjector = inject(EnvironmentInjector);
  private readonly _eventCardRefs: ComponentRef<unknown>[] = [];

  constructor() {
    super();
    effect(() => {
      this.events();
      untracked(() => this._destroyEventCards());
    });

    this.modes = {
      day: {
        dayStartTime: 6,
        dayEndTime: 22,
        hourHeight: 100,
        eventRenderer: this.eventRenderer,
      },
      week: {
        dayStartTime: 6,
        dayEndTime: 22,
        hourHeight: 100,
        eventRenderer: this.eventRenderer,
      },
    };
  }

  override ngOnInit(): void {
    super.ngOnInit();
    const externalEventSourceFeature = this.externalEventSourceFeature();
    if (typeof externalEventSourceFeature === 'boolean') {
      return;
    }
    this.externalEventSourceFeature.set({
      ...externalEventSourceFeature,
      getRecordFromElement: this.getRecordFromElement,
    });
  }

  ngAfterViewInit() {
    if (!this.calendarRef) {
      return;
    }
    const calendar = this.calendarRef.instance;
    if (calendar) {
      this._calendar.setApi(calendar, this.calendarContainer);
      this._calendar.setMode('week');

      this._registerSubscription(
        this._calendar.addEventRequest$.subscribe({
          next: event => this.addEventRequest.emit(event),
        })
      );

      this.calendarReady.emit(calendar);
      calendar.refresh();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._destroyEventCards();
    this._calendar.destroy();
  }

  public onBeforeDragMoveEnd(event: { eventRecord: CamEventRecord; newStartDate: Date; newEndDate: Date }) {
    this.updateEventRequest.emit({
      id: event.eventRecord.data.data.id,
      startDate: event.newStartDate,
      endDate: event.newEndDate,
    });
  }

  public getRecordFromElement = (element: HTMLElement): CamEventRecord | void => {
    if (!element) {
      return;
    }
    const duration = Number(element.dataset['duration']);
    return new CamEventRecord({
      brutJson: element.dataset['brut'],
      duration: isNaN(duration) || !duration ? 1 : duration,
      durationUnit: element.dataset['durationUnit'] || 'hour',
      iconCls: element.dataset['iconCls'] || '',
      id: element.dataset['id'] || '',
      name: element.dataset['name'] || '',
    });
  };

  public eventRenderer = ({ eventRecord }: { eventRecord: CamEventRecord }) => {
    const wrapper = document.createElement('div');

    const cmpRef = createComponent(this.cardComponent(), {
      environmentInjector: this._environmentInjector,
    });

    cmpRef.setInput('event', eventRecord.data);
    this._appRef.attachView(cmpRef.hostView);
    this._eventCardRefs.push(cmpRef);

    const domElem = (cmpRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
    wrapper.appendChild(domElem);

    return wrapper;
  };

  private _destroyEventCards(): void {
    this._eventCardRefs.forEach(ref => ref.destroy());
    this._eventCardRefs.length = 0;
  }
}
